import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Drawer, Empty, Spin, Tag, Timeline, Typography } from "antd";
import * as React from "react";
import { Button } from "src/components";
import { WebsiteSystemItem, useWebsiteSystemAudit } from "src/hooks/useWebsiteSystem";
import {
  formatAuditAction,
  formatAuditActorName,
  formatAuditCreatedAt,
  formatAuditFieldLabel,
  formatAuditFieldValue,
  formatAuditProduct,
} from "./auditFormat";

const { Text } = Typography;

const ACTION_COLOR: Record<string, string> = {
  create: "blue",
  edit: "geekblue",
  on: "green",
  off: "red",
  delete: "red",
  auto_off: "orange",
};

export type WebsiteSystemHistoryDrawerProps = {
  item: WebsiteSystemItem;
};

// "···" opens this - available to a view-only user too (BANNER-PAGE alone is
// enough for the audit GET's scope gate on the backend), unlike the edit modal.
export const WebsiteSystemHistoryDrawer = NiceModal.create(({ item }: WebsiteSystemHistoryDrawerProps) => {
  const { visible, hide, remove } = useModal();
  const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useWebsiteSystemAudit(item.code, visible);

  // Duplicate diffs are expected here, not a bug to fix: a PUT on a PER_PRODUCT
  // code writes one audit row per underlying product row, so a single edit of
  // GameError's shared text/schedule fields shows up as three back-to-back
  // entries with identical before/after - one per site.
  const entries = React.useMemo(() => data?.pages.flat() || [], [data]);

  return (
    <Drawer
      width={480}
      visible={visible}
      afterVisibleChange={(open) => {
        if (!open) remove();
      }}
      onClose={hide}
      title={`ประวัติการแก้ไข: ${item.name}`}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={hide}>ปิด</Button>
        </div>
      }
    >
      {isLoading && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      )}
      {isError && (
        <div style={{ textAlign: "center", padding: 24, color: "#ff4d4f" }}>
          {error instanceof Error ? error.message : "โหลดประวัติไม่สำเร็จ"}
        </div>
      )}
      {!isLoading && !isError && entries.length === 0 && <Empty description="ยังไม่มีประวัติการแก้ไข" />}
      {!isLoading && !isError && entries.length > 0 && (
        <Timeline>
          {entries.map((entry) => {
            // before/after are nullable on the wire (a "create"/"delete" action, or
            // any row this repo has not seen yet, might carry no diff at all) - an
            // empty object here renders the entry with no field lines rather than
            // throwing on Object.keys(null).
            const after = entry.after || {};
            const before = entry.before || {};
            const fields = Object.keys(after);
            // A stable composite, not the array index: resetQueries (see
            // useToggleWebsiteSystem/useUpdateWebsiteSystem) drops the drawer
            // back to its first page after an edit, so the entry that used to
            // be at a given index is not necessarily the same entry any more -
            // an index key would let React reuse that position's DOM/state for
            // what is now different data.
            return (
              <Timeline.Item
                key={`${entry.created_at}-${entry.product}-${entry.action}`}
                color={ACTION_COLOR[entry.action] || "blue"}
              >
                <div>
                  <Tag color={ACTION_COLOR[entry.action] || "blue"}>{formatAuditAction(entry.action)}</Tag>
                  <Tag>{formatAuditProduct(entry.product)}</Tag>
                </div>
                {fields.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {fields.map((field) => (
                      <div key={field}>
                        <Text type="secondary">{formatAuditFieldLabel(field)}: </Text>
                        {formatAuditFieldValue(field, before[field])}
                        {" → "}
                        {formatAuditFieldValue(field, after[field])}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    {formatAuditCreatedAt(entry.created_at)} • โดย {formatAuditActorName(entry.actor_name)}
                  </Text>
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      )}
      {hasNextPage && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
            โหลดเพิ่ม
          </Button>
        </div>
      )}
    </Drawer>
  );
});
